/**
 * BonificacionesPage — "Mis Reembolsos" del portal.
 *
 * Compone:
 *  - PageHeader con título + subtítulo.
 *  - Callout "¿Qué documentos necesito?" que abre RequiredDocsDialog.
 *  - CTA "Solicitar reembolso" → /bonificaciones/nueva.
 *  - Lista de ReimbursementCard o EmptyState.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import {
  StatusBadge,
  variantForStatus,
  labelForStatus,
} from '@app/components/common/StatusBadge';
import { RequiredDocsDialog } from '@app/components/reimbursements/RequiredDocsDialog';
import { useClaimsList } from '@modules/claims/presentation/hooks/useClaims';
import { usePetsList } from '@modules/pets/presentation/hooks/usePets';
import { formatCLP, formatDate } from '@shared/utils/formatters';
import {
  FileText,
  Sparkles,
  ChevronRight,
  Plus,
  PawPrint,
  ArrowRight,
} from 'lucide-react';

interface ReimbursementCardProps {
  claim: {
    id: string;
    claimNumber: string;
    submittedAt: string;
    status: string;
    diagnosis: string;
    totalAmountClp: number;
    approvedAmountClp?: number;
    petId: string;
  };
  petName?: string;
  onViewDetail: () => void;
}

function ReimbursementCard({ claim, petName, onViewDetail }: ReimbursementCardProps) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-xs transition-shadow hover:shadow-md sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              N° {claim.claimNumber}
            </span>
            <span aria-hidden="true" className="text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground">
              {formatDate(claim.submittedAt)}
            </span>
            <StatusBadge variant={variantForStatus(claim.status)}>
              {labelForStatus(claim.status)}
            </StatusBadge>
          </div>

          <div className="flex items-center gap-2 text-sm text-foreground">
            <PawPrint
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <span className="font-medium">{petName ?? 'Mascota'}</span>
          </div>

          <p className="line-clamp-2 text-sm text-muted-foreground">
            {claim.diagnosis}
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 pt-1">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Solicitado
              </p>
              <p className="text-sm font-semibold text-foreground">
                {formatCLP(claim.totalAmountClp)}
              </p>
            </div>
            {claim.approvedAmountClp != null && (
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Aprobado
                </p>
                <p className="text-sm font-semibold text-emerald-600">
                  {formatCLP(claim.approvedAmountClp)}
                </p>
              </div>
            )}
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="shrink-0 rounded-full"
          onClick={onViewDetail}
        >
          Ver detalle
          <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

export function BonificacionesPage() {
  const navigate = useNavigate();
  const [docsOpen, setDocsOpen] = useState(false);
  const { data: claims = [], isLoading: loadingClaims } = useClaimsList();
  const { data: pets = [] } = usePetsList();

  const petName = (petId: string) => pets.find((p) => p.id === petId)?.name;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mis Reembolsos"
        subtitle="Revisa y gestiona el estado de tus solicitudes"
      />

      {/* Callout documentos necesarios */}
      <button
        type="button"
        onClick={() => setDocsOpen(true)}
        className="flex w-full items-center gap-4 rounded-2xl border border-border bg-accent/40 p-5 text-left shadow-xs transition-all hover:bg-accent/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground">
          <Sparkles className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">¿Qué documentos necesito?</p>
          <p className="text-xs text-muted-foreground">
            Conoce los requisitos para cada tipo de reembolso.
          </p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      {/* CTA principal */}
      <Button
        className="w-full rounded-full sm:w-auto"
        size="lg"
        onClick={() => navigate('/bonificaciones/nueva')}
      >
        <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
        Solicitar reembolso
      </Button>

      {/* Lista o empty state */}
      {loadingClaims ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-36 w-full rounded-2xl" />
          ))}
        </div>
      ) : claims.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No tienes reembolsos aún"
          description="Cuando solicites tu primer reembolso aparecerá aquí y podrás hacer seguimiento de su estado."
        />
      ) : (
        <div className="space-y-3">
          {claims.map((claim) => (
            <ReimbursementCard
              key={claim.id}
              claim={claim}
              petName={petName(claim.petId)}
              onViewDetail={() => navigate(`/bonificaciones/${claim.id}`)}
            />
          ))}
        </div>
      )}

      <RequiredDocsDialog open={docsOpen} onOpenChange={setDocsOpen} />
    </div>
  );
}
